/**
 * Storage Queries
 * Functions for file upload, download, and management with Supabase Storage
 */

import { supabase } from '../supabase-client';

export interface UploadFileOptions {
  bucket: string;
  folder: string;
  file: File;
  fileName?: string;
  upsert?: boolean;
}

export interface DeleteFileOptions {
  bucket: string;
  path: string;
}

/**
 * Upload a file to Supabase Storage
 */
export async function uploadFile(options: UploadFileOptions): Promise<{ path: string; url: string }> {
  const { bucket, folder, file, fileName, upsert = false } = options;
  
  // Generate file name if not provided
  const timestamp = Date.now();
  const fileExt = file.name.split('.').pop();
  const finalFileName = fileName || `${timestamp}.${fileExt}`;
  const filePath = `${folder}/${finalFileName}`;

  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert,
    });

  if (error) {
    throw new Error(`Error uploading file: ${error.message}`);
  }

  // Get public URL if bucket is public
  const { data: { publicUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);

  return {
    path: data.path,
    url: publicUrl,
  };
}

/**
 * Delete a file from Supabase Storage
 */
export async function deleteFile(options: DeleteFileOptions): Promise<void> {
  const { bucket, path } = options;

  const { error } = await supabase.storage
    .from(bucket)
    .remove([path]);

  if (error) {
    throw new Error(`Error deleting file: ${error.message}`);
  }
}

/**
 * Get public URL for a file
 */
export function getFileUrl(bucket: string, path: string): string {
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  return data.publicUrl;
}

/**
 * Get signed URL for a private file (expires in 1 hour)
 */
export async function getSignedUrl(bucket: string, path: string, expiresIn = 3600): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    throw new Error(`Error creating signed URL: ${error.message}`);
  }

  return data.signedUrl;
}

/**
 * List files in a folder
 */
export async function listFiles(bucket: string, folder: string) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .list(folder, {
      limit: 100,
      offset: 0,
      sortBy: { column: 'created_at', order: 'desc' },
    });

  if (error) {
    throw new Error(`Error listing files: ${error.message}`);
  }

  return data;
}

/**
 * Download a file
 */
export async function downloadFile(bucket: string, path: string): Promise<Blob> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .download(path);

  if (error) {
    throw new Error(`Error downloading file: ${error.message}`);
  }

  return data;
}

/**
 * Check if a file exists
 */
export async function fileExists(bucket: string, path: string): Promise<boolean> {
  try {
    const files = await listFiles(bucket, path.split('/')[0]);
    const fileName = path.split('/').pop();
    return files.some(file => file.name === fileName);
  } catch {
    return false;
  }
}

/**
 * Get file metadata
 */
export async function getFileMetadata(bucket: string, path: string) {
  const files = await listFiles(bucket, path.split('/')[0]);
  const fileName = path.split('/').pop();
  return files.find(file => file.name === fileName);
}

